import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { readOntologyTerm, type OntologyModule, type TermDetails, type TermStatement } from "../../../api/content/ontologyApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { isAllowed } from "../../../api/access/decisions";
import { chosenProjectDecision, useProjects } from "../../../projects/projectContext";
import { useLoad } from "../../../shared/loading/useLoad";
import AppButton from "../../../ui/buttons/AppButton";
import CopyReference from "../../../ui/content/CopyReference";
import DiscussionPanel from "../../discussion/DiscussionPanel";
import { ChangeSetStepContext, type StepSave } from "../../processAuthoring/draft/changeSetStep";
import StepSaveNotice from "../../processAuthoring/draft/StepSaveNotice";
import { useStepBeforeThread } from "../../threads/prepare/author/editStep";
import ChangeSetPicker from "../../changeSets/ChangeSetPicker";
import type { ChangeSetChoice } from "../../changeSets/changeSetChoice";
import { AskInChatButton, SelectionToChat } from "../../chatReferences";
import { EditSessionBar, useEditSession } from "../../editing";
import AddSubclassDialog from "../proposals/AddSubclassDialog";
import { targetModuleOf, type TermEdit } from "../proposals/termPatches";
import { termEditCommit } from "../proposals/termProposals";
import { addedStatements, nextAddedKey, type StatementSection } from "./termEditing";
import { DetailSection, EntityChipList, EntityHeader, type EntityLookup } from "../viewer/detailParts";
import { orderedLabels } from "../viewer/ontologyModel";
import { compactIri } from "../viewer/types";
import TermRestrictionList from "./TermRestrictionList";
import { USUAL_DEFINITION_PREDICATES, USUAL_LABEL_PREDICATES } from "./statementText";
import TermStatementList, { type StatementEditing } from "./TermStatementList";
import TermUsageList from "./TermUsageList";
import Chip from "../../../ui/feedback/Chip";
import SourceView from "../../../ui/content/source/SourceView";
import LoadStatus from "../../../ui/feedback/LoadStatus";

interface TermDetailCardProps {
  projectId: string;
  commit: string;
  iri: string;
  /** Whether the version shown is the latest, the one proposals apply to. */
  canPropose: boolean;
  /** Every term IRI of the version, so a new term gets a free IRI. */
  knownIris: readonly string[];
  /** The modules of the version, to name the ones the term is declared in. */
  modules: readonly OntologyModule[];
  lookup: EntityLookup;
  /**
   * Where its edits are saved (ADR-0031 D2): on the Data model the card is the "Edit an item"
   * step's view before its Thread, and Save starts it; `shown` when a Thread's edit step shows
   * the card, and Save answers that step.
   */
  editStep: "beforeThread" | "shown";
}

/** One term at one model version, read from all its modules: what it is, how it relates,
 * where it is used, its Turtle, its discussion, and simple edits proposed for review. Its
 * labels and definitions are edited in place and proposed by one Save; selecting text in it
 * asks the agent about the term (F96). */
export default function TermDetailCard({ projectId, commit, iri, canPropose, knownIris, modules, lookup, editStep }: TermDetailCardProps) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readOntologyTerm(projectId, commit, iri, signal), [projectId, commit, iri]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("ontologyViewer.loadingTerm")} loadingClassName="py-10" onRetry={reload} />;
  return (
    <TermDetailsView projectId={projectId} details={state.value} canPropose={canPropose} knownIris={knownIris} modules={modules} lookup={lookup} editStep={editStep} />
  );
}

interface TermDetailsViewProps extends Omit<TermDetailCardProps, "commit" | "iri"> {
  details: TermDetails;
}

export function TermDetailsView({ projectId, details, canPropose, knownIris, modules, lookup, editStep }: TermDetailsViewProps) {
  const { t, i18n } = useTranslation();
  const projects = useProjects();
  const [addingSubclass, setAddingSubclass] = useState(false);
  const [saved, setSaved] = useState<StepSave | null>(null);
  const { term } = details;
  const name = orderedLabels(term.labels, i18n.language)[0]?.value ?? compactIri(term.iri);
  // What only inherited modules hold, such as the DMW basis's, is read-only here: an edit goes
  // into one of the project's own modules (`targetModuleOf`), statement by statement.
  const ownModules = new Set(modules.filter((module) => module.library === null).map((module) => module.reference));
  const ownModule = targetModuleOf(details, { kind: "addLabel", text: "", language: null }, ownModules);
  const ownTerm = ownModule !== null;
  const ownStatement = (statement: TermStatement) => targetModuleOf(details, { kind: "renameLabel", statement, text: "" }, ownModules) !== null;
  const mayPropose = ownTerm && isAllowed(chosenProjectDecision(projects, "propose_change"));
  const proposing = canPropose && mayPropose;
  const isProperty = term.kind === "object_property" || term.kind === "datatype_property" || term.kind === "annotation_property";
  const subject = { reference: term.iri, name };

  // Every edit is the "Edit an item" step's answer (ADR-0031 D2): the term's own module version
  // is the item it edits.
  const shownStep = useContext(ChangeSetStepContext);
  const beforeThread = useStepBeforeThread({
    projectId,
    title: t("threads.prepare.author.editTitle", { name }),
    edited: ownModule === null ? null : { kind: "term", module: ownModule, iri: term.iri },
  });
  const step = editStep === "shown" ? shownStep : beforeThread;

  // Labels and definitions are edited in place and proposed together by one Save (F96).
  const defaultTitle = t("ontologyViewer.proposals.titles.edits", { name });
  const [choice, setChoice] = useState<ChangeSetChoice>({ kind: "new", title: defaultTitle });
  const session = useEditSession<TermEdit>(async (edits) => {
    setSaved(await step.save(projectId, choice, [termEditCommit(projectId, details, edits, ownModules)]));
  });
  const editing = (section: StatementSection): StatementEditing | undefined =>
    proposing ? { section, session, termIri: term.iri, isEditable: ownStatement, projectId, subject } : undefined;
  const addAction = (label: string, section: StatementSection) =>
    proposing ? (
      <AppButton type="button" variant="ghost" size="xs" disabled={session.saving.status === "submitting"} onClick={() => { session.open(nextAddedKey(session, section)); }}>
        {label}
      </AppButton>
    ) : undefined;

  return (
    <SelectionToChat projectId={projectId} subject={subject} className="space-y-4">
      <EntityHeader
        name={name}
        iri={term.iri}
        badge={
          <>
            <Chip>{t(`ontologyViewer.termKinds.${term.kind}`)}</Chip>
            <AskInChatButton projectId={projectId} subject={subject} />
          </>
        }
      >
        <p className="app-text-muted flex flex-wrap items-center gap-x-1 text-xs">
          {t("ontologyViewer.declaredIn")}
          {details.declaredIn.map((module) => (
            <span key={module} className="inline-flex items-center">
              <Link className="app-action-link" to={appPaths.item(module)}>
                {modules.find((candidate) => candidate.reference === module)?.name ?? module}
              </Link>
              <CopyReference value={module} label={t("ontologyViewer.copyModule")} />
            </span>
          ))}
        </p>
      </EntityHeader>

      {saved !== null && saved.kind !== "nothingToChange" ? <StepSaveNotice saved={saved} shownIn={step.shownIn} /> : null}
      {!canPropose && mayPropose ? (
        <p className="app-text-muted text-xs">{t("ontologyViewer.proposals.latestOnly")}</p>
      ) : null}

      {saved?.kind === "nothingToChange" && session.saving.status === "succeeded" ? <StepSaveNotice saved={saved} shownIn={step.shownIn} /> : null}

      <DetailSection title={t("ontologyViewer.labels")} actions={addAction(t("ontologyViewer.proposals.addLabel"), "label")}>
        <TermStatementList statements={details.labels} emptyText={t("ontologyViewer.noLabels")} usualPredicates={USUAL_LABEL_PREDICATES} editing={editing("label")} />
      </DetailSection>
      <DetailSection
        title={t("ontologyViewer.definitions")}
        actions={details.definitions.length === 0 && addedStatements(session, "definition").length === 0 ? addAction(t("ontologyViewer.proposals.addDefinition"), "definition") : undefined}
      >
        <TermStatementList statements={details.definitions} emptyText={t("ontologyViewer.noDefinitions")} usualPredicates={USUAL_DEFINITION_PREDICATES} editing={editing("definition")} />
      </DetailSection>
      {term.kind !== "individual" ? (
        <>
          <DetailSection title={t("ontologyViewer.parents")}>
            <EntityChipList values={term.parents} lookup={lookup} emptyText={t("ontologyViewer.noParents")} />
          </DetailSection>
          <DetailSection
            title={t("ontologyViewer.children")}
            actions={
              term.kind === "class" && proposing ? (
                <AppButton type="button" variant="ghost" size="xs" onClick={() => { setAddingSubclass(true); }}>
                  {t("ontologyViewer.proposals.addSubclass")}
                </AppButton>
              ) : undefined
            }
          >
            <EntityChipList values={details.children} lookup={lookup} emptyText={t("ontologyViewer.noChildren")} />
          </DetailSection>
        </>
      ) : null}
      {isProperty ? (
        <>
          <DetailSection title={t("ontologyViewer.domain")}>
            <EntityChipList values={term.domain} lookup={lookup} emptyText={t("ontologyViewer.noDomain")} />
          </DetailSection>
          <DetailSection title={t("ontologyViewer.range")}>
            <EntityChipList values={term.range} lookup={lookup} emptyText={t("ontologyViewer.noRange")} />
          </DetailSection>
        </>
      ) : null}
      {term.kind === "class" ? (
        <DetailSection title={t("ontologyViewer.restrictions.title")}>
          <TermRestrictionList termName={name} restrictions={details.restrictions} lookup={lookup} />
        </DetailSection>
      ) : null}
      <DetailSection title={t("ontologyViewer.usage.title")}>
        <TermUsageList usage={details.usage} projectId={projectId} term={term} />
      </DetailSection>
      <details className="app-divider-top pt-4">
        <summary className="app-title cursor-pointer text-sm font-semibold">{t("ontologyViewer.termTurtle")}</summary>
        <SourceView label={t("ontologyViewer.termTurtle")} language="turtle" value={details.turtle} />
      </details>
      <DiscussionPanel
        projectId={projectId}
        subject={{ kind: "ontology_term", value: term.iri }}
        frame={(panel) => <DetailSection title={t("discussion.termTitle")}>{panel}</DetailSection>}
      />

      <EditSessionBar session={session} saveLabel={t("ontologyViewer.proposals.propose")} saveDisabled={choice.kind === "new" && choice.title.trim() === ""}>
        <ChangeSetPicker projectId={projectId} choice={choice} onChange={setChoice} defaultTitle={defaultTitle} />
      </EditSessionBar>

      {addingSubclass ? (
        <AddSubclassDialog
          projectId={projectId}
          details={details}
          knownIris={knownIris}
          ownModules={ownModules}
          step={step}
          onClose={() => {
            setAddingSubclass(false);
          }}
          onProposed={(proposal) => {
            setAddingSubclass(false);
            setSaved(proposal);
          }}
        />
      ) : null}
    </SelectionToChat>
  );
}
